import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Save, Send, Loader2, CheckCircle2, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { INTAKE_SECTIONS } from "../data/intakeSchema";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Field({ field, value, onChange, readOnly }) {
  const id = `intake-${field.key}`;
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <Label htmlFor={id}>{field.label}</Label>
        <Textarea id={id} rows={3} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        <Label htmlFor={id}>{field.label}</Label>
        <Input id={id} type="date" value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <Label>{field.label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(field.key, v)} disabled={readOnly}>
          <SelectTrigger className="mt-1.5" data-testid={id}><SelectValue placeholder="Choose..." /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "yesno") {
    return (
      <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-ivory px-4 py-3 sm:col-span-2">
        <span className="text-sm text-ink">{field.label}</span>
        <div className="flex gap-2">
          {["Yes", "No"].map((v) => (
            <button
              key={v}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(field.key, v)}
              data-testid={`${id}-${v.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-60 ${value === v ? (v === "Yes" ? "bg-champagne-dark text-white" : "bg-sage text-white") : "bg-surface border border-line text-mutedink"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "checks") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="sm:col-span-2">
        <Label>{field.label}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {field.options.map((o) => {
            const checked = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                disabled={readOnly}
                onClick={() => onChange(field.key, checked ? selected.filter((x) => x !== o) : [...selected, o])}
                data-testid={`${id}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-xs sm:text-sm transition-all disabled:opacity-60 ${checked ? "border-teal bg-teal-light text-teal font-medium" : "border-line bg-ivory text-ink hover:border-teal"}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-teal border-teal" : "border-line bg-surface"}`}>
                  {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
                </span>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "check") {
    return (
      <div className="sm:col-span-2">
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onChange(field.key, !value)}
          data-testid={id}
          className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-all disabled:opacity-60 w-full ${value ? "border-teal bg-teal-light" : "border-line bg-ivory hover:border-teal"}`}
        >
          <span className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 ${value ? "bg-teal border-teal" : "border-line bg-surface"}`}>
            {value && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
          </span>
          <span className={`text-sm ${value ? "text-teal font-medium" : "text-ink"}`}>{field.label}</span>
        </button>
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={id}>{field.label}{field.required && " *"}</Label>
      <Input id={id} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
    </div>
  );
}

export default function MyIntake() {
  const { refresh } = useAuth();
  const [intake, setIntake] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client-intakes/current")
      .then((r) => { setIntake(r.data); setAnswers(r.data.answers || {}); })
      .catch(() => toast.error("Could not load your intake."))
      .finally(() => setLoading(false));
  }, []);

  const readOnly = intake && ["SUBMITTED", "REVIEWED"].includes(intake.status);

  const setField = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));

  const sectionDone = useMemo(() => {
    return INTAKE_SECTIONS.map((s) =>
      s.fields.some((f) => {
        const v = answers[f.key];
        return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "" && v !== false;
      })
    );
  }, [answers]);

  const completedCount = sectionDone.filter(Boolean).length;
  const section = INTAKE_SECTIONS[sectionIdx];

  const save = async (submit) => {
    setSaving(true);
    try {
      const res = await api.put(`/client-intakes/${intake.intake_id}`, { answers, submit });
      if (submit) {
        toast.success("Your intake has been submitted securely. Your team will review it now.");
        setIntake({ ...intake, status: res.data.status, answers });
        refresh();
      } else {
        toast.success("Draft saved. You can come back anytime.");
        setIntake({ ...intake, status: res.data.status });
      }
    } catch (err) {
      console.error("Intake save failed", err);
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : err.response
            ? `Could not save (error ${err.response.status}). Please try again.`
            : "No connection — your answers are still on this page. Check your internet and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="portal-intake-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-teal" /> My Secure Intake
          </h1>
          <p className="mt-1 text-sm text-mutedink">Confidential. Your answers help your team build your Journey Plan — you can save and finish later.</p>
        </div>
        {intake && (
          <span className={`text-[10px] font-bold px-3 py-1.5 rounded-full w-fit ${intake.status === "SUBMITTED" || intake.status === "REVIEWED" ? "bg-sage-light text-sage" : "bg-champagne-light text-champagne-dark"}`} data-testid="intake-status-badge">
            {intake.status}
          </span>
        )}
      </div>

      {readOnly && (
        <div className="bg-sage-light border border-sage/30 rounded-2xl p-5 flex items-start gap-3" data-testid="intake-submitted-banner">
          <Lock className="h-5 w-5 text-sage flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-navy">Your intake has been {intake.status === "REVIEWED" ? "reviewed by your team" : "submitted"}.</p>
            <p className="text-xs text-mutedink mt-1">Need to change something? <Link to="/portal/messages" className="text-teal font-medium hover:underline">Send a secure message</Link> and your team will update it with you.</p>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="bg-surface border border-line rounded-2xl p-4" data-testid="intake-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-navy">{completedCount} of {INTAKE_SECTIONS.length} sections started</p>
            <p className="text-xs text-mutedink">Section {sectionIdx + 1}: {section.title}</p>
          </div>
          <div className="h-2 rounded-full bg-sand overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-teal to-sage transition-all" style={{ width: `${(completedCount / INTAKE_SECTIONS.length) * 100}%` }} />
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Section nav */}
        <nav className="lg:w-64 flex-shrink-0" aria-label="Intake sections">
          <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {INTAKE_SECTIONS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setSectionIdx(i)}
                data-testid={`intake-section-nav-${s.id}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-left text-xs sm:text-sm font-medium whitespace-nowrap lg:whitespace-normal transition-colors ${i === sectionIdx ? "bg-teal text-white" : sectionDone[i] ? "bg-sage-light text-sage" : "bg-surface border border-line text-mutedink hover:text-navy"}`}
              >
                <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i === sectionIdx ? "bg-white/20" : sectionDone[i] ? "bg-sage text-white" : "bg-sand text-mutedink"}`}>
                  {sectionDone[i] && i !== sectionIdx ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                </span>
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Section form */}
        <div className="flex-1 bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid={`intake-section-${section.id}`}>
          <h2 className="text-lg font-serif font-semibold text-navy">{section.title}</h2>
          {section.intro && <p className="mt-1.5 text-xs text-mutedink leading-relaxed">{section.intro}</p>}
          {readOnly ? (
            <dl className="mt-5 space-y-4" data-testid="intake-readonly-view">
              {section.fields.map((f) => {
                const v = answers[f.key];
                const display = Array.isArray(v) ? (v.length ? v.join(", ") : null)
                  : f.type === "check" ? (v ? "Confirmed" : null)
                  : (v ? String(v) : null);
                if (!display) return null;
                return (
                  <div key={f.key}>
                    <dt className="text-xs text-mutedink">{f.label}</dt>
                    <dd className="text-sm text-ink mt-0.5 whitespace-pre-wrap">{display}</dd>
                  </div>
                );
              })}
              {section.fields.every((f) => {
                const v = answers[f.key];
                return !(Array.isArray(v) ? v.length : v);
              }) && <p className="text-sm text-mutedink">No answers in this section.</p>}
            </dl>
          ) : (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {section.fields.map((f) => (
                <Field key={f.key} field={f} value={answers[f.key]} onChange={setField} readOnly={readOnly} />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => setSectionIdx(Math.max(0, sectionIdx - 1))}
              disabled={sectionIdx === 0}
              className="text-mutedink hover:text-white"
              data-testid="intake-prev-section"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous section
            </Button>
            <div className="flex gap-3">
              {!readOnly && (
                <Button variant="outline" onClick={() => save(false)} disabled={saving} className="border-line text-navy hover:bg-sand rounded-full" data-testid="intake-save-draft">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save draft
                </Button>
              )}
              {sectionIdx < INTAKE_SECTIONS.length - 1 ? (
                <Button onClick={() => setSectionIdx(sectionIdx + 1)} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="intake-next-section">
                  Next section <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : !readOnly && (
                <Button onClick={() => save(true)} disabled={saving} className="bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="intake-submit">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit securely
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
